import React, { useCallback } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { makeStyles } from "@material-ui/core/styles";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Divider from "@material-ui/core/Divider";
import InputAdornment from "@material-ui/core/InputAdornment";

import Button from "components/CustomButtons/Button";

const validationSchema = Yup.object().shape({
  position: Yup.string().required("Position is required"),
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  company: Yup.string().required("Company is required"),
  title: Yup.string().required("Title is required"),
  hoursPerWeek: Yup.number()
    .min(0, "Min value 0.")
    .required("Amount is required"),
  amount: Yup.number()
    .min(0, "Min value 0.")
    .required("Total FY compensation is required")
});

const useStyles = makeStyles(theme => ({
  dialogActions: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2)
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  marginRight: {
    marginRight: theme.spacing(3)
  }
}));

const defaultValues = {
  position: "",
  firstName: "",
  lastName: "",
  company: "",
  title: "",
  hoursPerWeek: 0,
  amount: 0
};

const ChapterBoardMemberDialog = ({
  open,
  data,
  onClose,
  onCreate,
  onUpdate
}) => {
  const classes = useStyles();

  const { item, isAdmin, isCreating } = data;

  const initialValues = Object.keys(item).length
    ? {
        ...item.officer,
        amount: item.amount,
        hoursPerWeek: item.hoursPerWeek
      }
    : defaultValues;

  const handleSubmit = useCallback(
    values => {
      isCreating ? onCreate(values, isAdmin) : onUpdate(values, isAdmin);
    },
    [isAdmin, isCreating]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Chapter Financial Information</DialogTitle>
      <DialogContent>
        <Grid container direction="column">
          <Typography className={classes.marginBottom}>
            <strong>{isCreating ? "Add" : "Edit"} a Board Member</strong>
          </Typography>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              isValid
            }) => {
              return (
                <Form autoComplete="off">
                  <Grid item>
                    <Grid container direction="column" spacing={2}>
                      <TextField
                        error={Boolean(touched.position && errors.position)}
                        helperText={touched.position && errors.position}
                        fullWidth
                        className={classes.marginBottom}
                        label="Position"
                        variant="outlined"
                        name="position"
                        value={values.position}
                        onChange={handleChange}
                      />
                      <TextField
                        error={Boolean(touched.firstName && errors.firstName)}
                        helperText={touched.firstName && errors.firstName}
                        fullWidth
                        className={classes.marginBottom}
                        label="First name"
                        variant="outlined"
                        name="firstName"
                        value={values.firstName}
                        onChange={handleChange}
                      />
                      <TextField
                        error={Boolean(touched.lastName && errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                        fullWidth
                        className={classes.marginBottom}
                        label="Last name"
                        variant="outlined"
                        name="lastName"
                        value={values.lastName}
                        onChange={handleChange}
                      />
                      <TextField
                        error={Boolean(touched.company && errors.company)}
                        helperText={touched.company && errors.company}
                        fullWidth
                        className={classes.marginBottom}
                        label="Company/Employer"
                        variant="outlined"
                        name="company"
                        value={values.company}
                        onChange={handleChange}
                      />
                      <TextField
                        error={Boolean(touched.title && errors.title)}
                        helperText={touched.title && errors.title}
                        fullWidth
                        className={classes.marginBottom}
                        label="Title"
                        variant="outlined"
                        name="title"
                        value={values.title}
                        onChange={handleChange}
                      />
                      <Divider className={classes.marginBottom} />
                      <TextField
                        error={Boolean(
                          touched.hoursPerWeek && errors.hoursPerWeek
                        )}
                        helperText={touched.hoursPerWeek && errors.hoursPerWeek}
                        fullWidth
                        className={classes.marginBottom}
                        label="Hours worked per week"
                        variant="outlined"
                        name="hoursPerWeek"
                        value={values.hoursPerWeek}
                        onChange={handleChange}
                      />
                      <TextField
                        error={Boolean(touched.amount && errors.amount)}
                        helperText={
                          touched.amount &&
                          errors.amount &&
                          "Enter a valid number."
                        }
                        fullWidth
                        label="Total FY compensation"
                        variant="outlined"
                        name="amount"
                        value={values.amount}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          )
                        }}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                  <div className={classes.dialogActions}>
                    <Button
                      className={classes.marginRight}
                      color="success"
                      variant="contained"
                      type="submit"
                    >
                      Submit
                    </Button>
                    <Button
                      color="danger"
                      variant="contained"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ChapterBoardMemberDialog;
